<template>
  <div class="event-chat-container">
    <div class="event-chat-header">
      <h3 class="title is-6">Chat del evento</h3>
      <b-button size="is-small" type="is-text" @click="$emit('close')">
        <b-icon icon="close" size="is-small"></b-icon>
      </b-button>
    </div>

    <div class="event-chat-messages" ref="messagesContainer">
      <b-loading :is-full-page="false" v-model="isLoading" :can-cancel="false"></b-loading>

      <div v-if="messages.length === 0 && !isLoading" class="has-text-centered has-text-grey mt-5">
        <p>No hay mensajes aún. ¡Sé el primero en saludar!</p>
      </div>

      <div
        v-for="message in messages"
        :key="message.id"
        class="message-item"
        :class="{ 'is-own-message': message.userSender.id === currentUserId }"
      >
        <div class="message-content">
          <div class="message-header">
            <span class="has-text-weight-semibold">{{ message.userSender.name }}</span>
            <span class="is-size-7 has-text-grey">{{ formatTime(message.sentAt) }}</span>
          </div>
          <p class="message-text">{{ message.message }}</p>
        </div>
      </div>
    </div>

    <div class="event-chat-input">
      <b-field>
        <b-input
          v-model="newMessage"
          placeholder="Escribe un mensaje..."
          type="textarea"
          rows="2"
          @keyup.native.enter.prevent="sendMessage"
          :disabled="isSending"
        ></b-input>
      </b-field>
      <b-button
        type="is-primary"
        @click="sendMessage"
        :disabled="!newMessage.trim() || isSending"
        :loading="isSending"
        class="send-button"
      >
        Enviar
      </b-button>
    </div>
  </div>
</template>

<script>
import eventChatService from "@/services/EventChat";
import { mapGetters } from "vuex";

export default {
  name: "EventChat",
  props: {
    eventId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      messages: [],
      newMessage: "",
      isLoading: false,
      isSending: false,
      pollingInterval: null,
    };
  },
  computed: {
    ...mapGetters({
      user: "getUser",
    }),
    currentUserId() {
      return this.user?.id;
    },
  },
  async mounted() {
    await this.loadMessages();
    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async loadMessages() {
      try {
        this.isLoading = true;
        const response = await eventChatService.getMessages(this.eventId);
        this.messages = response || [];
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        console.error("Error loading chat messages:", error);
        this.$buefy.toast.open({
          duration: 5000,
          message: "Error al cargar los mensajes del chat",
          type: "is-danger",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async sendMessage() {
      if (!this.newMessage.trim()) return;

      try {
        this.isSending = true;
        const message = this.newMessage.trim();

        // Optimistic update
        const tempMessage = {
          id: Date.now(),
          message: message,
          userSender: this.user,
          sentAt: new Date(),
        };
        this.messages.push(tempMessage);
        this.newMessage = "";
        this.$nextTick(() => {
          this.scrollToBottom();
        });

        await eventChatService.sendMessage(this.eventId, message);

        // Reload messages to get the correct server response
        await this.loadMessages();
      } catch (error) {
        console.error("Error sending message:", error);
        this.$buefy.toast.open({
          duration: 5000,
          message: "Error al enviar el mensaje",
          type: "is-danger",
        });
        // Revert optimistic update
        this.messages.pop();
      } finally {
        this.isSending = false;
      }
    },
    formatTime(dateString) {
      const date = new Date(dateString);
      return date.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
      }
    },
    startPolling() {
      // Poll for new messages every 5 seconds
      this.pollingInterval = setInterval(() => {
        this.loadMessages();
      }, 5000);
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.event-chat-container {
  display: flex;
  flex-direction: column;
  height: 500px;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.event-chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.event-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background-color: #fafafa;

  .message-item {
    margin-bottom: 1rem;

    &.is-own-message {
      .message-content {
        margin-left: auto;
        background-color: #e3f2fd;
        border-radius: 12px 12px 0 12px;
      }
    }

    &:not(.is-own-message) {
      .message-content {
        margin-right: auto;
        background-color: #ffffff;
        border-radius: 12px 12px 12px 0;
        border: 1px solid #e0e0e0;
      }
    }
  }

  .message-content {
    max-width: 70%;
    padding: 0.75rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .message-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.25rem;
    font-size: 0.875rem;
  }

  .message-text {
    margin: 0;
    word-wrap: break-word;
  }
}

.event-chat-input {
  display: flex;
  gap: 0.5rem;
  padding: 1rem;
  background-color: #f5f5f5;
  border-top: 1px solid #e0e0e0;

  .field {
    flex: 1;
    margin: 0;
  }

  .send-button {
    align-self: flex-end;
  }
}
</style>
